import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useUser } from "@/contexts/user";

export default function TermsOfService() {
  const navigate = useNavigate();
  const { user } = useUser();
  const isAuthenticated = !!user?.id;

  return (
    <div className="min-h-screen bg-background p-6 pb-24">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(isAuthenticated ? "/profile" : "/auth")}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {isAuthenticated ? "Back to Profile" : "Back to Login"}
        </Button>
        <Card className="p-8">
          <h1 className="text-3xl font-bold mb-6">Terms of Service</h1>
          <ScrollArea className="h-[calc(100vh-250px)]">
            <div className="space-y-6 text-muted-foreground pr-4">
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">1. Acceptance of Terms</h2>
                <p>By accessing and using StudyForge, you accept and agree to be bound by these Terms of Service. If you do not agree, please do not use our service.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">2. Description of Service</h2>
                <p>StudyForge is a gamified study timer and progress tracking application. We provide:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Study session tracking and timers</li>
                  <li>Leaderboard and social features</li>
                  <li>Progress analytics and statistics</li>
                  <li>Gamification elements (XP, levels, achievements)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">3. User Accounts</h2>
                <p>You are responsible for:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Maintaining the confidentiality of your account</li>
                  <li>All activities that occur under your account</li>
                  <li>Notifying us of any unauthorized use</li>
                  <li>Providing accurate information</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">4. Acceptable Use</h2>
                <p>You agree NOT to:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Manipulate or cheat the study tracking system</li>
                  <li>Use automated scripts or bots</li>
                  <li>Harass or abuse other users</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Use the service for any illegal purpose</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">5. Leaderboards and Social Features</h2>
                <p>By participating in leaderboards, you acknowledge that:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Your username and stats may be visible to other users</li>
                  <li>We may display your location (if provided)</li>
                  <li>Rankings are based on tracked study time</li>
                  <li>You can opt-out via "Solo Mode" in settings</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">6. Premium Features</h2>
                <p>Premium subscriptions:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Are billed monthly or annually</li>
                  <li>Auto-renew unless canceled</li>
                  <li>Include a 30-day money-back guarantee</li>
                  <li>Provide extended daily study limits and additional features</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">7. Intellectual Property</h2>
                <p>All content, features, and functionality of StudyForge are owned by us and protected by copyright, trademark, and other laws.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">8. Limitation of Liability</h2>
                <p>StudyForge is provided "as is" without warranties. We are not liable for any damages arising from your use of the service.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">9. Termination</h2>
                <p>We reserve the right to suspend or terminate accounts that violate these terms. You may delete your account at any time via profile settings.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">10. Changes to Terms</h2>
                <p>We may modify these terms at any time. Continued use after changes constitutes acceptance of new terms.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">11. Contact</h2>
                <p>For questions about these terms, use the feedback feature in the app or email legal@studyforge.app</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">12. Governing Law</h2>
                <p>These terms shall be governed by and construed in accordance with applicable laws. Any disputes arising from these terms or your use of StudyForge shall be resolved through good-faith negotiation. If negotiation fails, disputes shall be submitted to binding arbitration in accordance with applicable arbitration rules.</p>
              </section>

              <p className="text-sm mt-8 pt-6 border-t">
                Last updated: January 2025
              </p>
            </div>
          </ScrollArea>
        </Card>
      </div>
    </div>
  );
}
